import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Filter, X, SlidersHorizontal } from 'lucide-react';
import { ProductCard } from '../components/ProductCard';
import { products, ProductCategory, StockStatus } from '../data/products';
export function Shop() {
  const [activeCategory, setActiveCategory] = useState<ProductCategory | 'All'>('All');
  const [activeStatus, setActiveStatus] = useState<StockStatus | 'All'>('All');
  const [sortBy, setSortBy] = useState<'featured' | 'price-low' | 'price-high'>('featured');
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const categories: (ProductCategory | 'All')[] = ['All', 'Complete Systems', 'Bundles', 'Modules', 'Accessories'];
  const statuses: {
    val: StockStatus | 'All';
    label: string;
  }[] = [{
    val: 'All',
    label: 'All'
  }, {
    val: 'in-stock',
    label: 'In Stock'
  }, {
    val: 'pre-order',
    label: 'Pre-Order'
  }, {
    val: 'notify',
    label: 'Notify Me'
  }];
  const filteredProducts = useMemo(() => {
    let list = products.filter(p => {
      const cMatch = activeCategory === 'All' || p.category === activeCategory;
      const sMatch = activeStatus === 'All' || p.status === activeStatus;
      return cMatch && sMatch;
    });
    if (sortBy === 'price-low') list = [...list].sort((a, b) => a.price - b.price);
    if (sortBy === 'price-high') list = [...list].sort((a, b) => b.price - a.price);
    return list;
  }, [activeCategory, activeStatus, sortBy]);
  return <div className="bg-gray-50 min-h-screen" data-id="element-708">
      {/* Header */}
      <div className="bg-white border-b border-gray-200" data-id="element-709">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-id="element-710">
          <h1 className="text-3xl md:text-4xl font-extrabold text-yarbo-dark mb-2" data-id="element-711">
            Shop Yarbo Products
          </h1>
          <p className="text-gray-600 max-w-2xl" data-id="element-712">
            Complete systems, multi-module bundles, individual modules, and
            accessories. Online ordering with nationwide shipping.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-713">
        <div className="flex flex-col md:flex-row gap-8" data-id="element-714">
          {/* Mobile filter toggle */}
          <div className="md:hidden" data-id="element-715">
            <button onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)} className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 flex items-center justify-between font-bold text-sm" data-id="element-716">
              <span className="flex items-center gap-2" data-id="element-717">
                <SlidersHorizontal className="w-4 h-4" data-id="element-718" /> Filters & Sort
              </span>
              {isMobileFilterOpen ? <X className="w-4 h-4" data-id="element-719" /> : <Filter className="w-4 h-4" data-id="element-720" />}
            </button>
          </div>

          {/* Sidebar */}
          <aside className={`md:w-60 flex-shrink-0 ${isMobileFilterOpen ? 'block' : 'hidden md:block'}`} data-id="element-721">
            <div className="bg-white p-5 rounded-xl border border-gray-200 sticky top-24 space-y-6" data-id="element-722">
              <div data-id="element-723">
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-3" data-id="element-724">
                  Category
                </h3>
                <div className="space-y-1" data-id="element-725">
                  {categories.map(cat => <button key={cat} onClick={() => setActiveCategory(cat)} className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${activeCategory === cat ? 'bg-yarbo-dark text-white font-bold' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-726">
                      {cat}
                    </button>)}
                </div>
              </div>

              <div data-id="element-727">
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-3" data-id="element-728">
                  Availability
                </h3>
                <div className="space-y-1" data-id="element-729">
                  {statuses.map(s => <button key={s.val} onClick={() => setActiveStatus(s.val)} className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${activeStatus === s.val ? 'bg-yarbo-yellow/20 text-yarbo-dark font-bold' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-730">
                      {s.label}
                    </button>)}
                </div>
              </div>

              <button onClick={() => {
              setActiveCategory('All');
              setActiveStatus('All');
            }} className="w-full text-xs font-semibold text-gray-500 hover:text-yarbo-dark border-t border-gray-100 pt-4" data-id="element-731">
                Reset all filters
              </button>
            </div>
          </aside>

          {/* Grid */}
          <div className="flex-grow" data-id="element-732">
            <div className="flex justify-between items-center mb-5" data-id="element-733">
              <span className="text-sm text-gray-600 font-medium" data-id="element-734">
                Showing{' '}
                <span className="font-bold text-yarbo-dark" data-id="element-735">
                  {filteredProducts.length}
                </span>{' '}
                products
              </span>
              <select value={sortBy} onChange={e => setSortBy(e.target.value as any)} className="text-sm font-semibold border border-gray-200 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-yarbo-yellow focus:border-transparent outline-none" data-id="element-736">
                <option value="featured" data-id="element-737">Featured</option>
                <option value="price-low" data-id="element-738">Price: Low to High</option>
                <option value="price-high" data-id="element-739">Price: High to Low</option>
              </select>
            </div>

            {filteredProducts.length > 0 ? <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-id="element-740">
                {filteredProducts.map((product, idx) => <motion.div key={product.id} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.3,
              delay: Math.min(idx * 0.03, 0.4)
            }} data-id="element-741">
                    <ProductCard product={product} data-id="element-742" />
                  </motion.div>)}
              </div> : <div className="bg-white p-12 rounded-xl border border-gray-200 text-center" data-id="element-743">
                <h3 className="text-lg font-bold text-yarbo-dark mb-2" data-id="element-744">
                  No products match
                </h3>
                <button onClick={() => {
              setActiveCategory('All');
              setActiveStatus('All');
            }} className="text-sm font-bold text-yarbo-dark hover:underline" data-id="element-745">
                  Clear filters
                </button>
              </div>}
          </div>
        </div>
      </div>
    </div>;
}